import React, { useEffect, useState } from "react";
import "./Experience.css";

// EXACT bullet-point data: no reductions
import berkeleyLogo from "../images/uc_berkeley_eecs_logo.jpg";
import gotHubLogo from "../images/gothubai_logo.jpg";
import bartLogo from "../images/bart_logo.jpg";
import colossalLogo from "../images/colossal.jpg";
import deloitteLogo from "../images/deloitte_logo.jpg";
import calLogo from "../images/cal.png";
import calEcon from "../images/calEcon.png";
import thoughtlyLogo from "../images/thoughtly_logo.jpeg";

/** Your experiences array exactly as you gave it: **/
const experiences = [
  {
    logo: thoughtlyLogo,
    title: "Thoughtly",
    role: "Founding AI/ML Engineer",
    dates: "May 2024 - Present",
    bulletPoints: [
      "#1 hire at Thoughtly, 3M seed round. Built the entire AI/ML infrastructure.",
      "GenAI LLM Intelligence enhancement with RAG, few-shot prompting, and other dark magics.",
    ],
  },
  {
    logo: berkeleyLogo,
    title: "Berkeley EECS",
    role: "Researcher",
    dates: "Jan 2023 - Jan 2024",
    bulletPoints: [
      "Open-source project CktGym under the world’s best circuit lab with Professor Vladimir Stojanovic, funded by NSF and Intel.",
      "Designed, and developed a hardware RL based simulation system end-to-end, on GCP using Firebase for authentication and Intel16 and AlignHdl21 for circuit data generation, following MVC principles, on Linux VM.",
      "Designed data processing and monitoring pipeline with Spark and Pydantic.",
      "Designed, and developed entire infrastructure using OpenAI Gym and Ray for unsupervised learning.",
      "Integrated client-server interaction packages and reinforced learning capabilities using OpenAI Gym and Ray, with Firebase for user authentication and data security.",
      "Enhanced service performance and scalability through REST API with Flask and RPC, CI/CD pipeline with Github Actions.",
    ],
  },
  {
    logo: gotHubLogo,
    title: "GotHub.AI",
    role: "Founding Software Engineer",
    dates: "Jun 2023 - Mar 2024",
    bulletPoints: [
      "Designed and built the data engine in 2 months, won $300K Funding from MiraclePlus.",
      "Developed MultiAgent Framework with OpenAI, Llama2 and implemented Django framework, with a focus on scalability.",
      "Crafted frontend using Svelte with TypeScript, full stack development.",
      "Utilized AWS E2 Compute Engine for high-performance simulation, Cloud Storage for storing datasets and AI model outputs, and hacked Kubernetes to create customized JupyterLab for scaling services.",
      "Leveraged extensive use of GitHub actions for CI/CD pipeline.",
    ],
  },
  {
    logo: colossalLogo,
    title: "ColossalAI",
    role: "MLE Intern",
    dates: "Jan 2022 - Aug 2022",
    bulletPoints: [" GAN related stuff with PCA tries, too bad it already exists"],
  },
  {
    logo: bartLogo,
    title: "BART",
    role: "SWE Intern",
    dates: "Jan 2021 - May 2021",
    bulletPoints: [
      "Engineered a full-stack heat map application in Splunk for client, optimizing resource allocation using data.",
    ],
  },
  {
    logo: deloitteLogo,
    title: "Deloitte",
    role: "Consultant Intern (Tax)",
    dates: "Jun 2022 - Aug 2022",
    bulletPoints: [
      "Conducted quantitative analysis on financial data for Silicon Valley real estate companies, facilitating tax preparation and providing insights to optimize tax efficiency.",
    ],
  },
  {
    logo: calLogo,
    title: "UC Berkeley Statistics",
    role: "Undergraduate Student Instructor",
    dates: "May 2022 - Aug 2022",
    bulletPoints: ["uGSI for Stat20, Intro to Statistics"],
  },
  {
    logo: calEcon,
    title: "UC Berkeley HaaS Business School",
    role: "Research Assistant",
    dates: "Aug 2021 - Dec 2021",
    bulletPoints: [
      "Research Assistant for Michael Love under Professor Aaron Edlin",
      "Statistical analysis on large economics publication data to analyze trends in the econ development in the last decade",
      "Regression models, feature engineering, one-hot encoding",
    ],
  },
];

/** Helpers to compute date differences, so we can show a "gap" in months/years on hover **/
function parseDates(datesStr) {
  // Expect "MMM YYYY - MMM YYYY" or "MMM YYYY - Present"
  const [startStr, endStr] = datesStr.split("-").map((s) => s.trim());
  return {
    start: parseDate(startStr),
    end: parseDate(endStr),
  };
}
function parseDate(str) {
  if (!str) return new Date();
  if (str.toLowerCase().includes("present")) {
    return new Date(); // treat 'Present' as now
  }
  // e.g. "Jan 2023"
  const [monthAbbr, yearStr] = str.split(" ");
  const year = parseInt(yearStr, 10) || new Date().getFullYear();
  const monthMap = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  };
  return new Date(year, monthMap[monthAbbr] ?? 0);
}
function diffInMonths(d1, d2) {
  // returns integer # of months (could be negative if d2 < d1)
  const yearDiff = d2.getFullYear() - d1.getFullYear();
  const monthDiff = d2.getMonth() - d1.getMonth();
  return yearDiff * 12 + monthDiff;
}
function formatMonthDiff(m) {
  // e.g. 14 => "1y 2m"
  if (!m) return "";
  let negative = false;
  if (m < 0) {
    m = -m;
    negative = true;
  }
  const years = Math.floor(m / 12);
  const months = m % 12;
  const yStr = years ? `${years}y` : "";
  const mStr = months ? `${months}m` : "";
  const sep = yStr && mStr ? " " : "";
  const result = (yStr + sep + mStr) || "0m";
  return negative ? "-" + result : result;
}

const Experience = () => {
  // Optional fade in
  const [fadeIn, setFadeIn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFadeIn(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Pre-parse all the dates so we can easily compute gaps
  const allData = experiences.map((item) => {
    const { start, end } = parseDates(item.dates);
    return { ...item, startDate: start, endDate: end };
  });

  return (
    <div className={`experience-container ${fadeIn ? "fade-in" : ""}`}>
      {/* Subtle background lines (optional) */}
      <div className="animated-background"></div>

      <h1 className="experience-heading text-glow">Experience</h1>

      <div className="timeline">
        {allData.map((exp, idx) => {
          const isLast = idx === allData.length - 1;
          // Gap from this job's end -> next job's start
          let gapMonths = 0;
          if (!isLast) {
            const nextExp = allData[idx + 1];
            gapMonths = diffInMonths(exp.endDate, nextExp.startDate);
          }
          const gapLabel = formatMonthDiff(gapMonths);

          return (
            <div className="timeline-item" key={idx}>
              {/* Left column: the circle and vertical line to next item */}
              <div className="timeline-left">
                <div className="timeline-circle"></div>
                {/* Only show a connector line if it's not the last item */}
                {!isLast && (
                  <div className="timeline-line">
                    {/* Show the gap tooltip on hover */}
                    {/* {gapLabel && (
                      <div className="timeline-gap-tooltip">
                        Gap: {gapLabel}
                      </div>
                    )} */}
                  </div>
                )}
              </div>

              {/* Right column: the "card" containing your full bullet points */}
              <div className="experience-card">
                <div className="experience-card-header">
                  <img
                    src={exp.logo}
                    alt={exp.title}
                    className="experience-logo"
                  />
                  <div className="experience-card-info">
                    <h2 className="experience-title">{exp.title}</h2>
                    <p className="experience-dates">{exp.dates}</p>
                    <p className="experience-role">{exp.role}</p>
                  </div>
                </div>
                <ul className="experience-bullets">
                  {exp.bulletPoints.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Experience;
